/**
 * Web Mercator, and the conversion between a place on Earth and a place on this site.
 *
 * ## Why this module exists at all
 *
 * Before it, the reconstruction's very first act was a *guess*: stage 1 hunted the photograph for a
 * car or a door, assumed its real size, and derived metres-per-pixel from that. Every number
 * downstream — the site extent, every polygon, every planting spacing, every building height — was a
 * multiple of that guess, and the guess was routinely 20 % out.
 *
 * When the photograph comes from a map instead of a camera, that guess is unnecessary. A slippy-map
 * frame carries its own scale exactly: the projection is known, the zoom is known, the pixel size is
 * known, so metres-per-pixel is arithmetic rather than inference. Getting the scale for free is the
 * single largest accuracy win available in this feature, and it is the reason the map view is worth
 * building rather than just telling people to take a screenshot.
 *
 * ## The projection, and where it stops being true
 *
 * Web Mercator (EPSG:3857) is what every tile server on Earth serves, so it is what this speaks.
 * Its scale varies with latitude — a pixel at 60° N covers half the ground a pixel at the equator
 * does — and, less obviously, it varies *across* a frame from its top edge to its bottom.
 *
 * This module evaluates scale once, at the frame's centre latitude, and treats the frame as a plane.
 * Across 200 m — the largest site this tool models — the north-south error from that simplification
 * is under a centimetre at any temperate latitude, which is far below the resolution of anything
 * downstream. Over a whole city it would be wrong; the assumption is stated in
 * {@link metersPerPixel} rather than hidden, and {@link MAX_FLAT_FRAME_METERS} names where it stops
 * being safe.
 *
 * ## Axis convention
 *
 * A north-up frame maps onto the pipeline's world axes with no rotation and no sign surprises:
 * `+X` is east *and* image-right, `+Z` is south *and* image-down, matching the `[u, v]` convention
 * stage 1 already speaks. That is not a coincidence worth relying on silently, so
 * {@link latLngToSite} states it.
 *
 * Pure arithmetic — no DOM, no network, no provider knowledge.
 */

export interface LatLng {
  lat: number;
  lng: number;
}

/** Earth's radius at the equator, in metres — the sphere Web Mercator is defined on. */
export const EARTH_RADIUS_METERS = 6378137;

/** Ground metres covered by one pixel at zoom 0, latitude 0, on a 256 px tile. */
export const EQUATOR_METERS_PER_PIXEL = (2 * Math.PI * EARTH_RADIUS_METERS) / 256;

/** The latitude past which Web Mercator's y coordinate runs away to infinity. */
export const MERCATOR_MAX_LATITUDE = 85.05112878;

/**
 * How large a frame may get before treating it as flat stops being honest.
 *
 * Well above the 200 m the site model itself caps at, so in practice the check never fires — it is
 * here so that a future caller reaching for city-scale frames trips over the assumption instead of
 * inheriting it.
 */
export const MAX_FLAT_FRAME_METERS = 2000;

/**
 * Ground metres per screen pixel.
 *
 * `scale` is the imagery's pixel density — a `@2x` / `scale=2` request returns twice the pixels for
 * the same ground, so each pixel covers half as much. Evaluated at `lat` and used across the whole
 * frame; see the module note on why that is safe at site scale.
 */
export function metersPerPixel(lat: number, zoom: number, scale = 1): number {
  const clamped = clampLatitude(lat);
  return (EQUATOR_METERS_PER_PIXEL * Math.cos((clamped * Math.PI) / 180)) / (2 ** zoom * scale);
}

/**
 * The zoom that comes closest to fitting `meters` of ground across `pixels` of image, without
 * exceeding it — so a requested site always fits inside the frame rather than being cropped by it.
 */
export function zoomForExtent(lat: number, meters: number, pixels: number, scale = 1): number {
  const wanted = meters / Math.max(1, pixels);
  const exact =
    Math.log2((EQUATOR_METERS_PER_PIXEL * Math.cos((clampLatitude(lat) * Math.PI) / 180)) / wanted) -
    Math.log2(scale);
  return clamp(Math.floor(exact), 0, 22);
}

/** Global pixel coordinates at `zoom`, origin at the top-left of the world (180° W, 85.05° N). */
export function latLngToPixel(point: LatLng, zoom: number, tileSize = 256): { x: number; y: number } {
  const worldSize = tileSize * 2 ** zoom;
  const lat = clampLatitude(point.lat);
  const sin = Math.sin((lat * Math.PI) / 180);
  return {
    x: ((point.lng + 180) / 360) * worldSize,
    // The standard Mercator y, written as the log form rather than the tangent half-angle form
    // because it is the one every tile server's documentation states, and matching it exactly is
    // what keeps a frame registered with the tiles it was cut from.
    y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * worldSize,
  };
}

/** The inverse of {@link latLngToPixel}. */
export function pixelToLatLng(x: number, y: number, zoom: number, tileSize = 256): LatLng {
  const worldSize = tileSize * 2 ** zoom;
  const n = Math.PI - (2 * Math.PI * y) / worldSize;
  return {
    lng: (x / worldSize) * 360 - 180,
    lat: (180 / Math.PI) * Math.atan(Math.sinh(n)),
  };
}

/**
 * EPSG:3857 metres — the projected coordinates an ArcGIS or WMS `bbox` wants.
 *
 * Note these are *projected* metres, not ground metres: they are inflated by `1 / cos(lat)`, which
 * is why a bbox built from them describes the right rectangle while the numbers in it look too big.
 * Ground measurement is {@link metersPerPixel} and {@link distanceMeters}; this is only ever a
 * request parameter.
 */
export function latLngToMercatorMeters(point: LatLng): { x: number; y: number } {
  const lat = clampLatitude(point.lat);
  return {
    x: EARTH_RADIUS_METERS * ((point.lng * Math.PI) / 180),
    y: EARTH_RADIUS_METERS * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360)),
  };
}

/** The inverse of {@link latLngToMercatorMeters}. */
export function mercatorMetersToLatLng(x: number, y: number): LatLng {
  return {
    lng: wrapLongitude((x / EARTH_RADIUS_METERS) * (180 / Math.PI)),
    lat: (2 * Math.atan(Math.exp(y / EARTH_RADIUS_METERS)) - Math.PI / 2) * (180 / Math.PI),
  };
}

/**
 * A point on Earth as a point on the site, in the pipeline's world metres.
 *
 * Origin at the frame centre, `+X` east (image-right), `+Z` south (image-down) — the same axes
 * stages 2–5 work in, so a parcel boundary or an elevation reading lands in the same space as the
 * geometry the model produced, with no second convention to keep in step.
 */
export function latLngToSite(
  point: LatLng,
  center: LatLng,
  metersPerPixelAtCenter: number,
  zoom: number,
  tileSize = 256,
): { x: number; z: number } {
  const origin = latLngToPixel(center, zoom, tileSize);
  const target = latLngToPixel(point, zoom, tileSize);
  return {
    x: (target.x - origin.x) * metersPerPixelAtCenter,
    z: (target.y - origin.y) * metersPerPixelAtCenter,
  };
}

/** The inverse of {@link latLngToSite}. */
export function siteToLatLng(
  x: number,
  z: number,
  center: LatLng,
  metersPerPixelAtCenter: number,
  zoom: number,
  tileSize = 256,
): LatLng {
  const origin = latLngToPixel(center, zoom, tileSize);
  return pixelToLatLng(
    origin.x + x / metersPerPixelAtCenter,
    origin.y + z / metersPerPixelAtCenter,
    zoom,
    tileSize,
  );
}

/**
 * Great-circle distance in metres.
 *
 * Haversine rather than the planar approximation used elsewhere in this module, because this one is
 * also used to sanity-check a frame against a parcel that may have come from anywhere.
 */
export function distanceMeters(a: LatLng, b: LatLng): number {
  const toRad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * toRad;
  const dLng = (b.lng - a.lng) * toRad;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a.lat * toRad) * Math.cos(b.lat * toRad) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(s)));
}

/** Move a point by a ground offset in metres. East and south positive, matching the site axes. */
export function offsetLatLng(point: LatLng, eastMeters: number, southMeters: number): LatLng {
  const latPerMeter = 180 / (Math.PI * EARTH_RADIUS_METERS);
  const lat = point.lat - southMeters * latPerMeter;
  const lngPerMeter = latPerMeter / Math.max(1e-9, Math.cos((point.lat * Math.PI) / 180));
  return { lat: clampLatitude(lat), lng: wrapLongitude(point.lng + eastMeters * lngPerMeter) };
}

/**
 * The inverse of {@link offsetLatLng}: how far `point` lies east and south of `origin`, in metres.
 *
 * Uses `origin`'s latitude for the longitude scale, exactly as `offsetLatLng` does, so the pair
 * round-trips. That makes it the right tool for indexing *into* a grid whose cells were laid out by
 * `offsetLatLng` — the small-offset regime it is built for — and the wrong one for anything
 * continental, where `distanceMeters` is the honest answer.
 */
export function offsetBetween(
  origin: LatLng,
  point: LatLng,
): { eastMeters: number; southMeters: number } {
  const latPerMeter = 180 / (Math.PI * EARTH_RADIUS_METERS);
  const lngPerMeter = latPerMeter / Math.max(1e-9, Math.cos((origin.lat * Math.PI) / 180));
  // Normalized so a site straddling the antimeridian measures a few metres across rather than
  // 40,000 km.
  let dLng = point.lng - origin.lng;
  if (dLng > 180) dLng -= 360;
  if (dLng < -180) dLng += 360;
  return {
    eastMeters: dLng / lngPerMeter,
    southMeters: (origin.lat - point.lat) / latPerMeter,
  };
}

/** Axis-aligned geographic bounds. */
export interface LatLngBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

export function boundsOf(points: readonly LatLng[]): LatLngBounds | null {
  if (points.length === 0) return null;
  let north = -90;
  let south = 90;
  let east = -180;
  let west = 180;
  for (const point of points) {
    north = Math.max(north, point.lat);
    south = Math.min(south, point.lat);
    east = Math.max(east, point.lng);
    west = Math.min(west, point.lng);
  }
  return { north, south, east, west };
}

export function boundsCenter(bounds: LatLngBounds): LatLng {
  return { lat: (bounds.north + bounds.south) / 2, lng: (bounds.east + bounds.west) / 2 };
}

/** Ground size of a bounds, measured across its middle. */
export function boundsExtentMeters(bounds: LatLngBounds): { widthMeters: number; lengthMeters: number } {
  const midLat = (bounds.north + bounds.south) / 2;
  return {
    widthMeters: distanceMeters({ lat: midLat, lng: bounds.west }, { lat: midLat, lng: bounds.east }),
    lengthMeters: distanceMeters(
      { lat: bounds.south, lng: bounds.west },
      { lat: bounds.north, lng: bounds.west },
    ),
  };
}

/**
 * Read a coordinate a user typed or pasted.
 *
 * Accepts what people actually have in their clipboard: `42.36, -71.06`, a Google Maps URL with an
 * `@lat,lng,z` segment or a `?q=lat,lng` query, and degrees-minutes-seconds with hemisphere letters.
 * Returns null rather than guessing — a search box that silently flies to the wrong hemisphere is
 * worse than one that says it did not understand.
 */
export function parseLatLng(input: string): LatLng | null {
  const text = input.trim();
  if (text.length === 0) return null;

  // A Google Maps URL: the @ segment is the view centre, the q= parameter is the marked point.
  const at = /@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/.exec(text);
  const q = /[?&](?:q|query|ll)=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/.exec(text);
  const url = q ?? at;
  if (url) return validLatLng(Number(url[1]), Number(url[2]));

  const dms = parseDms(text);
  if (dms) return dms;

  const pair = /^(-?\d+(?:\.\d+)?)\s*[,;\s]\s*(-?\d+(?:\.\d+)?)$/.exec(text);
  if (pair) return validLatLng(Number(pair[1]), Number(pair[2]));

  return null;
}

/** `42°21'29.4"N 71°03'49.3"W` and its common punctuation variants. */
function parseDms(text: string): LatLng | null {
  const part = String.raw`(\d+)\s*[°d]\s*(\d+(?:\.\d+)?)?\s*['′m]?\s*(\d+(?:\.\d+)?)?\s*["″s]?\s*([NSEW])`;
  const match = new RegExp(`${part}[,\\s]+${part}`, 'i').exec(text);
  if (!match) return null;

  const first = dmsValue(match[1], match[2], match[3], match[4]);
  const second = dmsValue(match[5], match[6], match[7], match[8]);
  if (first === null || second === null) return null;

  // Either order is accepted, resolved by hemisphere letter rather than position.
  const firstIsLat = /[NS]/i.test(match[4] ?? '');
  return firstIsLat ? validLatLng(first, second) : validLatLng(second, first);
}

function dmsValue(
  degrees: string | undefined,
  minutes: string | undefined,
  seconds: string | undefined,
  hemisphere: string | undefined,
): number | null {
  if (degrees === undefined || hemisphere === undefined) return null;
  const value = Number(degrees) + Number(minutes ?? 0) / 60 + Number(seconds ?? 0) / 3600;
  if (!Number.isFinite(value)) return null;
  return /[SW]/i.test(hemisphere) ? -value : value;
}

function validLatLng(lat: number, lng: number): LatLng | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

/** `42.361500° N, 71.061200° W` — the form a readout should show, not the form a machine stores. */
export function formatLatLng(point: LatLng, places = 6): string {
  const lat = `${Math.abs(point.lat).toFixed(places)}° ${point.lat >= 0 ? 'N' : 'S'}`;
  const lng = `${Math.abs(point.lng).toFixed(places)}° ${point.lng >= 0 ? 'E' : 'W'}`;
  return `${lat}, ${lng}`;
}

export function clampLatitude(lat: number): number {
  return clamp(lat, -MERCATOR_MAX_LATITUDE, MERCATOR_MAX_LATITUDE);
}

export function wrapLongitude(lng: number): number {
  const wrapped = ((lng + 180) % 360 + 360) % 360;
  return wrapped - 180;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
